/**
 * DmsPageTrainingVideo component renders the training video page.
 * Displays trainings grouped by training with material lists.
 * @component
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DmsPage, fetchData, getToken, intl, HTTP_STATUS } from 'dmsbasereact';
import { formatDate } from '../../utils/formatters';
import {
    Typography,
    CircularProgress,
    Box,
    Fade,
    Alert,
    AlertTitle,
    Card,
    CardContent,
    Grid,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    LinearProgress,
    Divider,
} from '@mui/material';
import Swal from 'sweetalert2';
import { DmsTrainingVideo } from '../../components/DmsTrainingVideo/DmsTrainingVideo';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getBaseURL } from '../../build_config';
import { useInstitutionLinkDeclaration } from '../../hooks/useInstitutionLinkDeclaration';

const ACCOUNT_STATUS = {
    PENDING_TRAINING: 4,
    PENDING_RECYCLING_TRAINING: 10,
};

export const DmsPageTrainingVideo = () => {
    const [userType, setUserType] = useState(null);
    const [accountStatus, setAccountStatus] = useState(null);
    const { loading: declarationLoading, isDeclarationRequired, blockIfRequired } = useInstitutionLinkDeclaration(userType);

    useEffect(() => {
        const fetchUserTypeAndStatus = async () => {
            try {
                const response = await fetchData('/person/my_profile', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    const profileData = response.data?.data || response.data;
                    setUserType(profileData?.user_type || null);
                }
            } catch (err) {
                // User profile not accessible
            }

            try {
                const statusResponse = await fetchData('/account/status', 'GET', true);
                if (statusResponse.status === HTTP_STATUS.OK_200) {
                    setAccountStatus(statusResponse.data?.status);
                }
            } catch (err) {
                // Account status not accessible
            }
        };
        fetchUserTypeAndStatus();
    }, []);

    useEffect(() => {
        if (userType === 'doctor' && !declarationLoading) {
            blockIfRequired();
        }
    }, [userType, declarationLoading, blockIfRequired]);

    const { pk } = useParams();
    const navigate = useNavigate();
    const [training, setTraining] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availableTrainings, setAvailableTrainings] = useState([]);
    const [materialPk, setMaterialPk] = useState(null);
    const [completionDate, setCompletionDate] = useState(null);
    const [certificateExpiry, setCertificateExpiry] = useState(null);
    // Training context for multi-material awareness
    const [currentTrainingData, setCurrentTrainingData] = useState(null);

    useEffect(() => {
        let currentObjectUrl = null;

        const fetchAvailableTrainings = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetchData('/training_material/my-trainings', 'GET', true);
                const trainingsData = response.data?.trainings || [];

                if (trainingsData.length === 0) {
                    setAvailableTrainings([]);
                    setLoading(false);
                    return;
                }

                // If there's only one training with one material and no pk, redirect
                if (trainingsData.length === 1 && trainingsData[0].materials?.length === 1 && !pk) {
                    navigate(`/training-video/${trainingsData[0].materials[0].material_pk}`, { replace: true });
                    return;
                }

                setAvailableTrainings(trainingsData);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching available trainings:', err);
                setError(err.message || intl.get("ErrorLoadingTrainings").d("Error loading trainings"));
                setLoading(false);
            }
        };

        const fetchAllData = async () => {
            setLoading(true);
            setError(null);
            setTraining(null);
            setVideoUrl(null);
            setCompletionDate(null);
            setCertificateExpiry(null);
            setCurrentTrainingData(null);

            try {
                const materialRes = await fetchData(`/training_material/${pk}`, 'GET', true);
                const materialData = materialRes.data.data;

                if (!materialData) throw new Error(intl.get("TrainingMaterialNotFound").d("Training material not found."));

                const { training_pk, file_pk } = materialData;
                setMaterialPk(pk);

                const trainingRes = await fetchData(`/training/${training_pk}`, 'GET', true);
                setTraining(trainingRes.data.data);

                // Fetch full training data including sibling materials
                try {
                    const trainingsResponse = await fetchData('/training_material/my-trainings', 'GET', true);
                    const trainingsData = trainingsResponse.data?.trainings || [];
                    const parentTraining = trainingsData.find(t => t.training_pk === training_pk);

                    if (parentTraining) {
                        setCurrentTrainingData(parentTraining);

                        if (parentTraining.enrollment_status === 'Completed') {
                            // Only set completionDate if certificate is still valid
                            // When certificate is expired, leaving completionDate null
                            // allows the user to redo the training (same as recycling flow)
                            if (parentTraining.certificate_valid) {
                                setCompletionDate(parentTraining.completion_date);
                            }
                            setCertificateExpiry(parentTraining.certificate_expiry);
                        }
                    }
                } catch (completionErr) {
                    console.warn('Could not fetch completion status:', completionErr);
                }

                // Start training enrollment
                try {
                    await fetchData(`/training_material/${pk}/start`, 'POST', true);
                } catch (startErr) {
                    console.warn('Could not start training enrollment:', startErr);
                }

                const videoSrc = `${getBaseURL()}/training_material/file/${file_pk}`;
                const token = getToken();

                const videoRes = await fetch(videoSrc, {
                    headers: { Authorization: `Bearer ${token.access_token}` }
                });
                if (!videoRes.ok) throw new Error(`Failed to load video: ${videoRes.statusText}`);

                const blob = await videoRes.blob();
                currentObjectUrl = URL.createObjectURL(blob);
                setVideoUrl(currentObjectUrl);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (pk) {
            fetchAllData();
        } else {
            fetchAvailableTrainings();
        }

        return () => {
            if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
        };
    }, [pk, navigate]);

    // Show "already completed" swal
    useEffect(() => {
        if (completionDate && accountStatus !== null && accountStatus !== ACCOUNT_STATUS.PENDING_RECYCLING_TRAINING) {
            const formatDateTime = (isoString) => {
                if (!isoString) return '';
                return new Date(isoString).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            };

            Swal.fire({
                title: intl.get("TrainingAlreadyCompleted").d("Training Already Completed"),
                html: `<p>${intl.get("FinishedAt").d("Finished at")}: <strong>${formatDateTime(completionDate)}</strong></p>`,
                icon: 'info',
                confirmButtonColor: '#3085d6',
                confirmButtonText: intl.get("Ok").d("Ok")
            });
        }
    }, [completionDate, accountStatus]);

    const renderContent = () => {
        if (userType === 'doctor' && (declarationLoading || isDeclarationRequired)) {
            return (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh"
                    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <CircularProgress size={60} sx={{ color: 'white' }} />
                </Box>
            );
        }

        if (loading) {
            return (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh"
                    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <Fade in={loading} timeout={600}>
                        <div style={{ textAlign: 'center' }}>
                            <CircularProgress size={60} thickness={4}
                                sx={{ mb: 3, color: 'white', '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
                            <Typography variant="h6" color="white"
                                sx={{ fontWeight: 500, textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                                {intl.get("LoadingContent").d("Loading training experience...")}
                            </Typography>
                        </div>
                    </Fade>
                </Box>
            );
        }

        if (error) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height="100vh" p={3}
                    sx={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)' }}>
                    <Fade in={!!error} timeout={800}>
                        <div>
                            <Alert severity="error"
                                sx={{ width: '100%', maxWidth: '600px', borderRadius: 3, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
                                <AlertTitle sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                    {intl.get("ErrorLoadingVideo").d("Error Loading Video")}
                                </AlertTitle>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {error}
                                </Typography>
                            </Alert>
                        </div>
                    </Fade>
                </Box>
            );
        }

        // No trainings available
        if (!pk && availableTrainings.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <OndemandVideoIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        {intl.get("NoTrainingMaterialsAvailable").d("No training materials available")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {intl.get("NoTrainingMaterialsDescription").d("There are no training materials available for your profile at this time. Please contact your administrator if you believe this is an error.")}
                    </Typography>
                </Box>
            );
        }

        // Training list view - grouped by training
        if (!pk && availableTrainings.length > 0) {
            return (
                <Box sx={{ p: 4 }}>
                    <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
                        {intl.get("AvailableTrainings").d("Available Trainings")}
                    </Typography>
                    <Grid container spacing={3}>
                        {availableTrainings.map((trainingItem) => (
                            <Grid item xs={12} md={6} key={trainingItem.training_pk}>
                                <Card sx={{
                                    height: '100%',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
                                    border: trainingItem.enrollment_status === 'Completed'
                                        ? (trainingItem.certificate_valid === false ? '2px solid #ff9800' : '2px solid #4caf50')
                                        : 'none'
                                }}
                                onClick={() => {
                                    if (trainingItem.materials?.length > 0) {
                                        navigate(`/training-video/${trainingItem.materials[0].material_pk}`);
                                    }
                                }}>
                                    <CardContent>
                                        {/* Training header */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            {trainingItem.enrollment_status === 'Completed' && trainingItem.certificate_valid === false ? (
                                                <WarningAmberIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                                            ) : trainingItem.enrollment_status === 'Completed' ? (
                                                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                                            ) : (
                                                <OndemandVideoIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" component="div">
                                                    {trainingItem.training_name || 'Training'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                                    {trainingItem.is_mandatory !== undefined && (
                                                        <Chip size="small"
                                                            label={trainingItem.is_mandatory
                                                                ? intl.get("Mandatory").d("Mandatory")
                                                                : intl.get("Optional").d("Optional")}
                                                            color={trainingItem.is_mandatory ? "error" : "info"}
                                                            variant={trainingItem.is_mandatory ? "filled" : "outlined"} />
                                                    )}
                                                    {trainingItem.enrollment_status && (
                                                        <Chip size="small"
                                                            label={
                                                                trainingItem.enrollment_status === 'Completed' && trainingItem.certificate_valid === false
                                                                    ? intl.get("CertificateExpired").d("Certificate Expired")
                                                                    : trainingItem.enrollment_status === 'Completed'
                                                                        ? intl.get("Completed").d("Completed")
                                                                        : trainingItem.enrollment_status === 'InProgress'
                                                                            ? intl.get("InProgress").d("In Progress")
                                                                            : intl.get("NotStarted").d("Not Started")}
                                                            color={
                                                                trainingItem.enrollment_status === 'Completed' && trainingItem.certificate_valid === false ? 'warning'
                                                                    : trainingItem.enrollment_status === 'Completed' ? 'success'
                                                                        : trainingItem.enrollment_status === 'InProgress' ? 'warning'
                                                                            : 'default'} />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {trainingItem.training_description || intl.get("NoDescriptionAvailable").d("No description available")}
                                        </Typography>

                                        {/* Progress bar */}
                                        {trainingItem.materials && trainingItem.materials.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {intl.get("VideosWatched").d("Videos watched")}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {trainingItem.watched_count}/{trainingItem.mandatory_count}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={trainingItem.mandatory_count > 0
                                                        ? (trainingItem.watched_count / trainingItem.mandatory_count) * 100
                                                        : 100}
                                                    sx={{ borderRadius: 1, height: 6 }} />
                                            </Box>
                                        )}

                                        {/* Materials list */}
                                        {trainingItem.materials && trainingItem.materials.length > 0 && (
                                            <>
                                                <Divider sx={{ mb: 1 }} />
                                                <List dense disablePadding>
                                                    {trainingItem.materials.map((material) => (
                                                        <ListItem
                                                            key={material.material_pk}
                                                            sx={{
                                                                borderRadius: 1, px: 1, cursor: 'pointer',
                                                                '&:hover': { backgroundColor: 'action.hover' }
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/training-video/${material.material_pk}`);
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                                {material.watched ? (
                                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                                ) : (
                                                                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                                                )}
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={material.file_name}
                                                                primaryTypographyProps={{ variant: 'body2' }}
                                                            />
                                                            <ListItemSecondaryAction>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {material.is_mandatory && (
                                                                        <Chip size="small" label={intl.get("Mandatory").d("Mandatory")}
                                                                            color="error" variant="outlined"
                                                                            sx={{ height: 20, fontSize: '0.65rem' }} />
                                                                    )}
                                                                    <Chip size="small"
                                                                        label={material.watched
                                                                            ? intl.get("Watched").d("Watched")
                                                                            : intl.get("NotWatched").d("Not watched")}
                                                                        color={material.watched ? "success" : "default"}
                                                                        variant="outlined"
                                                                        sx={{ height: 20, fontSize: '0.65rem' }} />
                                                                    <IconButton size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate(`/training-video/${material.material_pk}`);
                                                                        }}>
                                                                        <PlayCircleOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </ListItemSecondaryAction>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}

                                        {trainingItem.certificate_expiry && (
                                            <Typography
                                                variant="caption"
                                                color={trainingItem.certificate_valid ? "success.main" : "warning.main"}
                                                sx={{ display: 'block', mt: 1 }}
                                            >
                                                {trainingItem.certificate_valid
                                                    ? `${intl.get("CertificateValidUntil").d("Certificate valid until")}: ${formatDate(trainingItem.certificate_expiry)}`
                                                    : `${intl.get("CertificateExpiredOn").d("Certificate expired on")}: ${formatDate(trainingItem.certificate_expiry)}`
                                                }
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            );
        }

        if (!training || !videoUrl) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height="100vh"
                    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <Fade in={true} timeout={800}>
                        <div style={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="white"
                                sx={{ fontWeight: 500, textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                                {intl.get("TrainingMaterialNotFound").d("Training material not found.")}
                            </Typography>
                        </div>
                    </Fade>
                </Box>
            );
        }

        const isMainTraining = accountStatus === ACCOUNT_STATUS.PENDING_TRAINING;

        return (
            <DmsTrainingVideo
                training={training}
                videoUrl={videoUrl}
                isTraining={true}
                materialPk={materialPk}
                completionDate={completionDate}
                certificateExpiry={certificateExpiry}
                isMainTraining={isMainTraining}
                accountStatus={accountStatus}
                trainingData={currentTrainingData}
            />
        );
    };

    return (
        <DmsPage name={training ? training.name : intl.get("Training").d("Training")}>
            {renderContent()}
        </DmsPage>
    );
};
